import { CloseOutlined } from "@ant-design/icons";
import { DataSourceType, Writeback, WritebackMode, WritebackScheduleExportType } from "@coeff/api";
import { WBFieldMappingsIcon } from "@coeff/components";
import React from "react";
import styled from "styled-components";

import { useWritebacksSchema } from "../api";
import { COLORS, SQL_DATASOURCES } from "../constants";
import { ScheduleText } from "../routes/CreateCloudTable/ScheduleText";
import { columnToLetter, computeSelectedRowsText } from "../utils";

import {
  Card as _Card,
  CardHeader as _CardHeader,
  CurvedArrow,
  ImportTypeIcon,
  Settings,
  Tooltip,
  Date,
  TargetIcon,
  FlexColumn,
  FlexRow,
  TextWithIcon,
  Typography,
  Button,
  SettingsBars,
  Paper,
  PaperContent,
} from ".";

const TopMessage = styled.div`
  margin: 22px auto;
  text-align: center;

  & p:last-child {
    margin-bottom: 0 !important;
  }
`;

const FancyImage = styled.div`
  width: 160px;
  height: 40px;
  margin: 0 auto;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;

    span,
    svg {
      height: 40px;
      width: 40px;
    }
  }

  .green {
    background: ${COLORS.lightGreenBg};

    svg {
      margin: auto;
      height: 25px;
    }
  }

  .myarrow {
    transform: rotate(90deg);
  }

  .line {
    border-right: 0.2rem dashed ${COLORS.black15};
    display: inline-block;
    height: 4rem;
  }

  .arrow {
    position: absolute;
    top: -0.3rem;
    bottom: 0;
    height: 0.65rem;
    border-right: 0.2rem solid ${COLORS.black15};
    display: inline-block;
  }

  .right {
    left: 0.2rem;
    transform: rotate(-45deg);
  }

  .left {
    right: 0.2rem;
    transform: rotate(45deg);
  }
`;

const Card = styled(_Card)``;

const CardHeader = styled(_CardHeader)``;

const StyledPanel = styled.div`
  padding: 10px 10px 20px 10px;

  &:focus,
  &:active {
    outline: none;
  }
`;

const StyledCardHeader = styled(_CardHeader)`
  min-height: 18px;
  height: auto;
`;

const SummaryPanel = styled.div`
  margin: 10px 0px 0px 25px;

  & p:last-child {
    margin-bottom: 0 !important;
  }
`;

const SummaryText = styled.p`
  color: ${COLORS.black45};
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 22px;
  margin-bottom: 5px;
  // Let long unbreakable destination/table names wrap instead of overflowing the panel. (ENG-7771)
  overflow-wrap: break-word;
`;

const TextBold = styled.span`
  color: ${COLORS.black85} !important;
`;

const StyledList = styled.ul`
  list-style: none;
  margin: 20px 0 -10px;
  padding: 0px;

  li {
    background-color: white;
    border-radius: 2px;
    padding: 5px;
    margin-bottom: 8px;
    line-height: 1;

    &.hidden {
      label {
        color: ${COLORS.black45};
      }
    }

    &:first-child {
      margin-top: 0px;
    }

    &:last-child {
      margin-bottom: 0px;
    }
  }
`;

const ColumnSelectField = styled.li`
  display: flex;
  flex-direction: column;

  &:hover {
    & .field-actions {
      display: block;
    }
  }
`;

const FieldLabel = styled.label`
  display: block;
  color: ${COLORS.black45};
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 17px;
  width: calc(100% - 20px);
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin-bottom: 7px;
`;

const FieldSelect = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 16px;
  margin-left: 5px;
`;

const FieldInfo = styled.div`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  & div {
    display: inline;
    color: ${COLORS.black85};
    font-size: 14px;
    font-weight: bold;
    letter-spacing: 0;
    line-height: 17px;
    margin-left: 2px;
    cursor: pointer;
  }

  & svg {
    color: ${COLORS.black45};
  }

  .dataSourceTypeIcon {
    width: 16px;
    height: 16px;
    line-height: 16px;
    margin-right: 4px;

    svg {
      width: 100%;
      height: 100%;
    }
  }
`;

const FieldLabelTooltip = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0px;
  background: rgba(255, 255, 255, 0.4);
  z-index: 10;
`;

const ACTIONS: Array<{ label: string; value: WritebackMode; description: string }> = [
  { label: "Update", value: "update", description: "Updates data for existing records" },
  { label: "Insert", value: "insert", description: "Creates a NEW ID and inserts data" },
  {
    label: "Upsert",
    value: "upsert",
    description:
      "Updates data for existing records or if the record does not already exist it creates a NEW ID and inserts data",
  },
  {
    label: "Insert with line items",
    value: WritebackMode.InsertWithLineItems,
    description: "Insert new records with line items into NetSuite from selected rows.",
  },
  {
    label: "Update with line items",
    value: WritebackMode.UpdateWithLineItems,
    description: "Update existing records' line items in NetSuite from selected rows.",
  },
  { label: "Delete", value: "delete", description: "Deletes data for existing records" },
  { label: "Add to list", value: "add_to_list", description: "Adds contacts to a list" },
  {
    label: "Remove from list",
    value: "remove_from_list",
    description: "Removes contacts from a list",
  },
  {
    label: "Sync list",
    value: "sync_list",
    description: "Updates HubSpot to match the sheet",
  },
  { label: "Void", value: "void", description: "Voids the selected object" },
];

type Props = {
  writeback?: Writeback;
  onClose?: (value?: string) => void;
};

export const WritebackDetailsSidebarContent: React.FC<Props> = ({ writeback, onClose }) => {
  const headerRow: undefined | number = (() => {
    if (!writeback?.header_range_a1) {
      return;
    }

    const rows = writeback?.header_range_a1.match(/\d+/g);

    return rows ? Number(rows[0]) : undefined;
  })();

  const { data: { object_types } = {} } = useWritebacksSchema(
    { dataSourceId: writeback?.data_source_id! },
    { enabled: Boolean(writeback?.data_source_id) }
  );

  const { data: { object_types: objectTypesWithFields } = {} } = useWritebacksSchema(
    { dataSourceId: writeback?.data_source_id!, objectName: writeback?.object_type_name },
    { enabled: Boolean(writeback?.data_source_id && writeback?.object_type_name) }
  );
  const fields = objectTypesWithFields?.[0]?.fields;

  const selectedObjectType = object_types?.find(
    objectType => objectType.object_name === writeback?.object_type_name
  );

  const getObjectLanguageText = (
    dataSourceType?: DataSourceType,
    capitalize?: boolean,
    pluralize?: boolean
  ): string => {
    if (dataSourceType && SQL_DATASOURCES.includes(dataSourceType)) {
      return `${capitalize ? "T" : "t"}able${pluralize ? "s" : ""}`;
    }
    return `${capitalize ? "O" : "o"}bject${pluralize ? "s" : ""}`;
  };

  return (
    <div style={{ overflowY: "auto" }}>
      <div style={{ margin: 15, marginBottom: 20 }}>
        <FlexColumn>
          <FlexRow style={{ justifyContent: "space-between" }}>
            <TextWithIcon
              title={
                <Typography variant="body1" fontWeight={700}>
                  Settings
                </Typography>
              }
              icon={<SettingsBars />}
            />

            {onClose ? (
              <Button type="text" onClick={() => onClose()}>
                <CloseOutlined />
              </Button>
            ) : null}
          </FlexRow>

          <Paper variant="info">
            <PaperContent size="xsmall">
              <Typography color={COLORS.black65}>
                <Typography fontWeight={600}>READ-ONLY</Typography>
                Open in spreadsheet to make changes.
              </Typography>
            </PaperContent>
          </Paper>
        </FlexColumn>
      </div>

      <div style={{ position: "relative" }}>
        <Overlay />

        <TopMessage>
          <FancyImage>
            <TargetIcon addonPlatform={writeback?.target_type ?? "google_sheets"} size="40" />

            <div className="myarrow">
              <span className="arrow left"></span>
              <span className="line"></span>
              <span className="arrow right"></span>
            </div>

            <div className="circle" style={{ marginLeft: "8px" }}>
              <ImportTypeIcon type={writeback?.data_source_type} />
            </div>
          </FancyImage>
        </TopMessage>

        <Card>
          <StyledPanel>
            <StyledCardHeader
              icon={
                <TargetIcon addonPlatform={writeback?.target_type ?? "google_sheets"} size="16" />
              }
              title="Source Data"
            />
            <SummaryPanel>
              <>
                <SummaryText>
                  Tab:{" "}
                  <TextBold>
                    {writeback?.google_sheets_tab_name ?? writeback?.excel_sheet_name ?? ""}
                  </TextBold>
                </SummaryText>

                <SummaryText>
                  Header Row: <TextBold> Row {headerRow}</TextBold>
                </SummaryText>
              </>
            </SummaryPanel>
          </StyledPanel>
        </Card>

        <Card>
          <StyledPanel>
            <StyledCardHeader
              icon={<ImportTypeIcon type={writeback?.data_source_type} />}
              title="Destination"
            />

            <SummaryPanel>
              <SummaryText>
                {getObjectLanguageText(writeback?.data_source_type, true)}:{" "}
                <TextBold>
                  {selectedObjectType?.object_label ??
                    selectedObjectType?.object_name ??
                    writeback?.object_type_name}
                </TextBold>
              </SummaryText>

              <SummaryText>
                Action:{" "}
                <TextBold>
                  {ACTIONS.find(a => a.value === writeback?.writeback_mode)?.label}
                </TextBold>
              </SummaryText>
            </SummaryPanel>
          </StyledPanel>
        </Card>

        {["delete", "void"].includes(writeback?.writeback_mode ?? "") && (
          <Card>
            <StyledPanel>
              <StyledCardHeader
                icon={<WBFieldMappingsIcon color="black" />}
                title="Field Mappings"
              />

              <SummaryPanel>
                <SummaryText>
                  <div>Column that contains the ID:</div>
                  <TextBold>{writeback?.id_ss_column_name}</TextBold>
                </SummaryText>
              </SummaryPanel>
            </StyledPanel>
          </Card>
        )}

        {!["delete", "void"].includes(writeback?.writeback_mode ?? "") && (
          <Card>
            <StyledPanel>
              <StyledCardHeader
                icon={<WBFieldMappingsIcon color="black" />}
                title="Field Mappings"
              />

              <div id="mapColumns">
                <StyledList>
                  {(writeback?.mappings ?? []).map((m, i) =>
                    !m.ss_column_name || !m.source_field_name ? null : (
                      <ColumnSelectField key={i}>
                        <FieldLabel>{m.ss_column_name}</FieldLabel>

                        <FieldSelect>
                          <FieldInfo>
                            <CurvedArrow />
                            {m.source_field_name ? (
                              <Tooltip
                                mouseEnterDelay={0.5}
                                title={
                                  <FieldLabelTooltip>
                                    <span>{m.source_field_name}</span>
                                  </FieldLabelTooltip>
                                }
                              >
                                <div>
                                  <ImportTypeIcon type={writeback?.data_source_type} />{" "}
                                  {fields?.find(field => field.field_name === m.source_field_name)
                                    ?.field_label ??
                                    m.source_field_label ??
                                    m.source_field_name}
                                </div>
                              </Tooltip>
                            ) : null}
                          </FieldInfo>
                        </FieldSelect>
                      </ColumnSelectField>
                    )
                  )}
                </StyledList>
              </div>
            </StyledPanel>
          </Card>
        )}

        <Card>
          <StyledPanel>
            <StyledCardHeader icon={<Settings />} title="Advanced Settings" />
            <SummaryPanel>
              <SummaryText>
                Column for results:{" "}
                <TextBold>
                  Col{" "}
                  {writeback?.result_ss_column_index || writeback?.result_ss_column_index === 0
                    ? columnToLetter(writeback?.result_ss_column_index + 1)
                    : ""}
                </TextBold>
              </SummaryText>
              {writeback?.data_source_type === "salesforce" && (
                <SummaryText>
                  Batch size: <TextBold>{writeback?.salesforce_batch_size}</TextBold>
                </SummaryText>
              )}
              {writeback?.data_source_type === "salesforce" &&
                ["Account", "Case", "Lead"].includes(writeback?.object_type_name) && (
                  <SummaryText>
                    Disable Salesforce Auto Assign:{" "}
                    <TextBold>
                      {writeback?.disable_salesforce_auto_assign ? "True" : "False"}
                    </TextBold>
                  </SummaryText>
                )}
              <SummaryText>
                Export empty cells:{" "}
                <TextBold>{writeback?.export_empty_cells ? "Enabled" : "Disabled"}</TextBold>
              </SummaryText>
            </SummaryPanel>
          </StyledPanel>
        </Card>

        <Card className={writeback?.options?.schedule ? "green" : ""}>
          <StyledPanel>
            <StyledCardHeader
              icon={<Date fill={COLORS.black85} />}
              title="Export Schedule"
            ></StyledCardHeader>
            <SummaryPanel>
              {!writeback?.options?.schedule && <SummaryText>Not scheduled</SummaryText>}
              {writeback?.options?.schedule && (
                <>
                  <SummaryText>
                    <ScheduleText schedule={writeback?.options.schedule}></ScheduleText>
                  </SummaryText>
                  <SummaryText>
                    <TextBold>
                      {!writeback?.options.schedule_export_type ||
                      writeback?.options.schedule_export_type ===
                        WritebackScheduleExportType.AllRows
                        ? "All rows on sheet"
                        : `${writeback?.options.schedule_selected_row_indices?.length ?? 0} row${
                            (writeback?.options.schedule_selected_row_indices ?? []).length > 1
                              ? "s"
                              : ""
                          } `}{" "}
                      will be exported
                    </TextBold>
                  </SummaryText>
                  {writeback?.options.schedule_export_type ===
                    WritebackScheduleExportType.SelectedRows &&
                    writeback?.options.schedule_selected_row_indices?.length && (
                      <SummaryText>
                        {computeSelectedRowsText(
                          writeback?.options.schedule_selected_row_indices ?? []
                        )}
                      </SummaryText>
                    )}
                  {!!writeback?.options.condition_column_name && (
                    <SummaryText>
                      Only export if value in column{" "}
                      <TextBold>{writeback?.options.condition_column_name} </TextBold>
                      is true
                    </SummaryText>
                  )}
                </>
              )}
            </SummaryPanel>
          </StyledPanel>
        </Card>
      </div>
    </div>
  );
};
